/**
 * A07 — Compte de Résultat: FE form ↔ BE situation_compte_resultat column mapping.
 * Writable columns only (excludes id, created_at, updated_at).
 */

import { getBrandDefaultCurrency } from '../../../shared/utils/brandCurrency';

export const COMPTE_RESULTAT_WRITABLE_FIELDS = [
  'resultat_ref',
  'exercice_fiscal',
  'periode_debut',
  'periode_fin',
  'date_etablissement',
  'type_periode',
  'statut_resultat',
  'devise',
  'taux_conversion',
  'chiffre_affaires',
  'ventes_produits',
  'prestations_services',
  'production_stockee',
  'production_immobilisee',
  'subventions_exploitation',
  'total_produits_exploitation',
  'achats_matieres',
  'variation_stocks_matieres',
  'autres_achats',
  'charges_externes',
  'impots_taxes',
  'charges_personnel',
  'dotations_amortissements',
  'autres_charges_exploitation',
  'total_charges_exploitation',
  'resultat_exploitation',
  'produits_financiers',
  'charges_financieres',
  'resultat_financier',
  'produits_exceptionnels',
  'charges_exceptionnelles',
  'resultat_exceptionnel',
  'participation_salaries',
  'impots_benefices',
  'resultat_net',
  'resultat_par_action',
  'marge_brute',
  'marge_brute_taux',
  'excedent_brut_exploitation',
  'ebitda',
  'ebit',
  'capacite_autofinancement',
  'capacite_autofinancement_net',
  'ratios_rentabilite',
  'ratios_productivite',
  'comparatif_precedent',
  'variation_ca',
  'variation_resultat',
  'analyse_charges',
  'analyse_produits',
  'seuil_rentabilite',
  'point_mort_atteint',
  'commentaires_exploitation',
  'commentaires_financier',
  'commentaires_exceptionnel',
  'notes_explicatives',
  'annexes',
  'validation_controle_gestion',
  'validation_daf',
  'validation_dg',
  'conformite_pcn',
  'conformite_ifrs',
  'notes',
  'metadata',
  'created_by',
];

const DATE_FIELDS = new Set([
  'periode_debut',
  'periode_fin',
  'date_etablissement',
]);

const DECIMAL_FIELDS = new Set([
  'taux_conversion',
  'chiffre_affaires',
  'ventes_produits',
  'prestations_services',
  'production_stockee',
  'production_immobilisee',
  'subventions_exploitation',
  'total_produits_exploitation',
  'achats_matieres',
  'variation_stocks_matieres',
  'autres_achats',
  'charges_externes',
  'impots_taxes',
  'charges_personnel',
  'dotations_amortissements',
  'autres_charges_exploitation',
  'total_charges_exploitation',
  'resultat_exploitation',
  'produits_financiers',
  'charges_financieres',
  'resultat_financier',
  'produits_exceptionnels',
  'charges_exceptionnelles',
  'resultat_exceptionnel',
  'participation_salaries',
  'impots_benefices',
  'resultat_net',
  'resultat_par_action',
  'marge_brute',
  'marge_brute_taux',
  'excedent_brut_exploitation',
  'ebitda',
  'ebit',
  'capacite_autofinancement',
  'capacite_autofinancement_net',
  'variation_ca',
  'variation_resultat',
  'seuil_rentabilite',
]);

const JSON_FIELDS = new Set([
  'ratios_rentabilite',
  'ratios_productivite',
  'comparatif_precedent',
  'analyse_charges',
  'analyse_produits',
  'notes_explicatives',
  'annexes',
  'metadata',
]);

const TEXT_DEFAULTS = Object.freeze({
  type_periode: 'Annuel',
  statut_resultat: 'Provisoire',
  devise: getBrandDefaultCurrency(),
  point_mort_atteint: 'Non',
  validation_controle_gestion: 'Non',
  validation_daf: 'Non',
  validation_dg: 'Non',
  conformite_pcn: 'Non',
  conformite_ifrs: 'Non',
});

export const EMPTY_COMPTE_RESULTAT_FORM = Object.freeze({
  ...COMPTE_RESULTAT_WRITABLE_FIELDS.reduce((acc, field) => {
    if (JSON_FIELDS.has(field)) {
      acc[field] = {};
      return acc;
    }
    if (TEXT_DEFAULTS[field] !== undefined) {
      acc[field] = TEXT_DEFAULTS[field];
      return acc;
    }
    if (field === 'taux_conversion') {
      acc[field] = 1;
      return acc;
    }
    acc[field] = '';
    return acc;
  }, {}),
});

const normalizeDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.split('T')[0];
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  return String(value);
};

const parseJsonField = (value) => {
  if (value == null || value === '') return {};
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const toFormScalar = (field, value) => {
  if (value == null) {
    if (JSON_FIELDS.has(field)) return {};
    if (TEXT_DEFAULTS[field] !== undefined) return TEXT_DEFAULTS[field];
    if (field === 'taux_conversion') return 1;
    return '';
  }
  if (JSON_FIELDS.has(field)) return parseJsonField(value);
  if (DATE_FIELDS.has(field)) return normalizeDate(value);
  return value;
};

/** Map API row → form state (all writable columns). */
export const recordToFormData = (record = {}) => {
  const form = { ...EMPTY_COMPTE_RESULTAT_FORM };
  COMPTE_RESULTAT_WRITABLE_FIELDS.forEach((field) => {
    if (record[field] !== undefined && record[field] !== null) {
      form[field] = toFormScalar(field, record[field]);
    }
  });
  return form;
};

const resolveDateEtablissement = (formData) => {
  const explicit = normalizeDate(formData.date_etablissement);
  if (explicit) return explicit;
  const periodeFin = normalizeDate(formData.periode_fin);
  if (periodeFin) return periodeFin;
  const periodeDebut = normalizeDate(formData.periode_debut);
  if (periodeDebut) return periodeDebut;
  return new Date().toISOString().split('T')[0];
};

/** Map form state → API payload (typed for situation_compte_resultat). */
export const buildCompteResultatPayload = (formData = {}) => {
  const payload = {};

  COMPTE_RESULTAT_WRITABLE_FIELDS.forEach((field) => {
    const raw = formData[field];

    if (DECIMAL_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? 0 : parseFloat(raw) || 0;
      return;
    }

    if (JSON_FIELDS.has(field)) {
      payload[field] = parseJsonField(raw);
      return;
    }

    if (DATE_FIELDS.has(field)) {
      if (field === 'date_etablissement') {
        payload[field] = resolveDateEtablissement(formData);
        return;
      }
      payload[field] = raw === '' || raw == null ? null : normalizeDate(raw);
      return;
    }

    if (TEXT_DEFAULTS[field] !== undefined) {
      payload[field] = raw === '' || raw == null ? TEXT_DEFAULTS[field] : raw;
      return;
    }

    payload[field] = raw === '' || raw == null ? null : raw;
  });

  return payload;
};
